import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod";
import type { Config } from "../../config/schema.js";
import type { ILibraryOperations } from "../../features/operations/library-operations.js";
import {
  type SortField,
  type SortOrder,
  sortFieldSchema,
  sortOrderSchema,
} from "../../features/pagination/index.js";
import { pickDefined } from "../../utils/object.js";

export interface ListToolParams {
  sort?: SortField | undefined;
  order?: SortOrder | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  includeSuperseded?: boolean | undefined;
}

/**
 * Register the list tool with the MCP server.
 *
 * @param server - The MCP server instance
 * @param getLibraryOperations - Function to get the current library operations instance
 * @param getConfig - Function to get the current config
 */
export function registerListTool(
  server: McpServer,
  getLibraryOperations: () => ILibraryOperations,
  getConfig: () => Config
): void {
  server.registerTool(
    "list",
    {
      description:
        "List references in the library. Returns raw CslItem[] data. Supports sorting and " +
        "pagination. References marked as superseded (custom.superseded_by) are omitted unless " +
        "includeSuperseded is set — cite the successor instead.",
      inputSchema: {
        sort: sortFieldSchema
          .optional()
          .describe("Sort by field: created, updated, published, author, title (default: updated)"),
        order: sortOrderSchema.optional().describe("Sort order: asc or desc (default: desc)"),
        limit: z
          .number()
          .int()
          .min(0)
          .optional()
          .describe("Maximum number of results (0 = no limit)"),
        offset: z
          .number()
          .int()
          .min(0)
          .optional()
          .describe("Number of results to skip (default: 0)"),
        includeSuperseded: z
          .boolean()
          .optional()
          .describe(
            "Include references marked as superseded by another (default: false). " +
              "Their custom.superseded_by holds the successor's uuid."
          ),
      },
    },
    async (args: ListToolParams) => {
      const libraryOps = getLibraryOperations();
      const config = getConfig();

      // Apply default limit from config if not specified
      const limit = args.limit ?? config.mcp.defaultLimit;

      const result = await libraryOps.list({
        limit,
        ...pickDefined(args, ["sort", "order", "offset", "includeSuperseded"] as const),
      });

      return {
        content: [
          {
            type: "text" as const,
            text: JSON.stringify({
              total: result.total,
              limit: result.limit,
              offset: result.offset,
              nextOffset: result.nextOffset,
              items: result.items,
            }),
          },
        ],
      };
    }
  );
}
